import React from 'react'

import { useTranslation } from '@/features/locale/LocaleProvider'
import { cn } from '@/lib/utils'
import { nutrimaticDevice } from '@/nutrimatic'
import type { PriceImpactDisplayState } from '../../utils/priceImpactDisplay'

export function PriceImpactReadout({
  display,
  limitIsPairSpecific,
  variant = 'prominent',
  className,
  label,
}: {
  display: PriceImpactDisplayState
  limitIsPairSpecific: boolean
  variant?: 'prominent' | 'compact' | 'ledger' | 'telemetry' | 'inline' | 'instrument'
  className?: string
  label?: string
}) {
  const t = useTranslation()

  if (display.zone === 'idle' && !display.impactPct) {
    return null
  }

  const heading = label ?? t.swapUi.priceImpact
  const phrase =
    display.zone === 'idle'
      ? null
      : t.swapUi.priceImpactGuide[display.zone]
  const limitText = limitIsPairSpecific
    ? t.swapUi.priceImpactGuide.limitPair(display.limitPct)
    : t.swapUi.priceImpactGuide.limitGlobal(display.limitPct)

  if (variant === 'instrument') {
    return (
      <div
        className={cn(
          nutrimaticDevice.readoutDisplaySlot,
          display.nutrimaticSlotClass,
          className,
        )}
      >
        <span className={nutrimaticDevice.readoutDisplaySlotLabel}>{heading}</span>
        <span className={cn(nutrimaticDevice.readoutDisplaySlotValue, display.toneClass)}>
          {display.impactPct != null ? `${display.impactPct}%` : '—'}
        </span>
        {phrase ? (
          <span className="mt-0.5 block text-[10px] leading-snug text-muted-foreground/90">{phrase}</span>
        ) : null}
      </div>
    )
  }

  if (variant === 'inline') {
    return (
      <div className={cn('space-y-0.5', className)}>
        <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{heading}</div>
        <div className={cn('font-mono text-sm tabular-nums', display.toneClass)}>
          {display.impactPct != null ? `${display.impactPct}%` : '—'}
        </div>
        {phrase ? <p className="text-[10px] leading-snug text-muted-foreground/90">{phrase}</p> : null}
        <p className="text-[10px] text-muted-foreground/75">{limitText}</p>
      </div>
    )
  }

  if (variant === 'compact' || variant === 'ledger' || variant === 'telemetry') {
    const isTelemetry = variant === 'telemetry'

    return (
      <div
        className={cn(
          'swap-price-impact-readout',
          'swap-price-impact-readout--ledger',
          isTelemetry && 'swap-price-impact-readout--telemetry',
          display.readoutClass,
          className,
        )}
        role="status"
        aria-live="polite"
      >
        <dl className="swap-price-impact-readout__sheet">
          <div className="swap-price-impact-readout__sheet-row swap-price-impact-readout__sheet-row--primary">
            <dt className="swap-price-impact-readout__label">{heading}</dt>
            <dd className={cn('swap-price-impact-readout__value', display.toneClass)}>
              {display.impactPct != null ? `${display.impactPct}%` : '—'}
            </dd>
          </div>
          {isTelemetry && (phrase || limitText) ? (
            <div className="swap-price-impact-readout__sheet-row swap-price-impact-readout__sheet-row--meta">
              {phrase ? <dd className="swap-price-impact-readout__phrase">{phrase}</dd> : null}
              <dd className="swap-price-impact-readout__limit">{limitText}</dd>
            </div>
          ) : (
            <>
              {phrase ? (
                <div className="swap-price-impact-readout__sheet-row swap-price-impact-readout__sheet-row--note">
                  <dd className="swap-price-impact-readout__phrase">{phrase}</dd>
                </div>
              ) : null}
              <div className="swap-price-impact-readout__sheet-row swap-price-impact-readout__sheet-row--limit">
                <dd className="swap-price-impact-readout__limit">{limitText}</dd>
              </div>
            </>
          )}
        </dl>
      </div>
    )
  }

  return (
    <div
      className={cn('swap-price-impact-readout', display.readoutClass, className)}
      role="status"
      aria-live="polite"
    >
      <div className="swap-price-impact-readout__row">
        <span className="swap-price-impact-readout__label">{heading}</span>
        <span className={cn('swap-price-impact-readout__value', display.toneClass)}>
          {display.impactPct != null ? `${display.impactPct}%` : '—'}
        </span>
      </div>
      {phrase ? <p className="swap-price-impact-readout__phrase">{phrase}</p> : null}
      <p className="swap-price-impact-readout__limit">{limitText}</p>
    </div>
  )
}
